import { useTranslation } from 'react-i18next';
import { UseFormRegister, FieldValues, Path } from 'react-hook-form';
import { FormInput } from '../ui/FormInput';

interface AddressErrors {
  street?: { message?: string };
  postalCode?: { message?: string };
  city?: { message?: string };
  country?: { message?: string };
  additionalInfo?: { message?: string };
}

interface AddressFormProps<T extends FieldValues> {
  prefix: string;
  register: UseFormRegister<T>;
  errors: AddressErrors;
  showOptionalInfo?: boolean;
}

export const AddressForm = <T extends FieldValues>({
  prefix,
  register,
  errors,
  showOptionalInfo = true,
}: AddressFormProps<T>) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <FormInput
        label={t(`company.address.street.label`)}
        type="text"
        placeholder={t(`company.address.street.placeholder`)}
        error={errors.street?.message}
        required
        {...register(`${prefix}.street` as Path<T>, {
          required: t(`company.address.street.error.required`),
        })}
      />

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <FormInput
            label={t(`company.address.postal_code.label`)}
            type="text"
            placeholder={t(`company.address.postal_code.placeholder`)}
            error={errors.postalCode?.message}
            required
            {...register(`${prefix}.postalCode` as Path<T>, {
              required: t(`company.address.postal_code.error.required`),
            })}
          />
        </div>
        <div className="flex-1">
          <FormInput
            label={t(`company.address.city.label`)}
            type="text"
            placeholder={t(`company.address.city.placeholder`)}
            error={errors.city?.message}
            required
            {...register(`${prefix}.city` as Path<T>, {
              required: t(`company.address.city.error.required`),
            })}
          />
        </div>
      </div>

      <FormInput
        label={t(`company.address.country.label`)}
        type="text"
        placeholder={t(`company.address.country.placeholder`)}
        error={errors.country?.message}
        required
        {...register(`${prefix}.country` as Path<T>, {
          required: t(`company.address.country.error.required`),
        })}
      />

      {showOptionalInfo && (
        <FormInput
          label={t(`company.address.additional_info.label`)}
          type="text"
          placeholder={t(`company.address.additional_info.placeholder`)}
          error={errors.additionalInfo?.message}
          {...register(`${prefix}.additionalInfo` as Path<T>)}
        />
      )}
    </div>
  );
};
